import AgoraRTC, {
  IAgoraRTCClient,
  IAgoraRTCRemoteUser,
  IRemoteAudioTrack,
  IRemoteVideoTrack,
} from 'agora-rtc-sdk-ng';
import { getBrowserFingerprint } from '@/utils/browserFingerprint';
import { rsaPublicDecryptPkcs1 } from '@/utils/rsaPublicDecrypt';

const RTC_APP_ID_PUBLIC_KEY = process.env.VUE_APP_RTC_APP_ID_PUBLIC_KEY || '';

export type GuestRtcPayload = {
  conferenceId: string;
  roomId: string;
  token: string;
  appId: string;
  touristsDuration?: string;
  uid?: string | number;
  sessionId?: string;
};

let rtcClient: IAgoraRTCClient | null = null;
let joinedConferenceId = '';
let leavingRtc = false;
const remoteAudioTracks = new Map<string, IRemoteAudioTrack>();
const remoteVideoTracks = new Map<string, IRemoteVideoTrack>();
let audioAutoplayHandlerBound = false;
let remoteAudioScanTimer: number | null = null;
let remoteMediaScanTimer: number | null = null;
let currentGuestSession: GuestRtcSessionMarker | null = null;
let sessionBroadcastChannel: BroadcastChannel | null = null;

export type GuestRtcVideoState = {
  hasVideo: boolean;
  uid?: string | number;
};

export type GuestRtcAudioState = {
  speakingUids: Array<string | number>;
  unmutedUids: Array<string | number>;
};

type GuestRtcVideoListener = (state: GuestRtcVideoState) => void;
type GuestRtcAudioListener = (state: GuestRtcAudioState) => void;
type GuestRtcSessionReplaceListener = (marker: GuestRtcSessionMarker) => void;
type GuestRtcTokenExpiredListener = (state: GuestRtcTokenExpiredState) => void;
type GuestRtcUserStateChangeListener = (state: GuestRtcUserStateChange) => void;

export type GuestRtcTokenExpiredState = {
  conferenceId: string;
  reason: string;
};

export type GuestRtcUserStateChange = {
  conferenceId: string;
  reason: string;
  uid?: string | number;
};

type GuestRtcSessionMarker = {
  conferenceId: string;
  roomId: string;
  uid: string;
  sessionId: string;
  timestamp: number;
};

const videoListeners = new Set<GuestRtcVideoListener>();
const audioListeners = new Set<GuestRtcAudioListener>();
const sessionReplaceListeners = new Set<GuestRtcSessionReplaceListener>();
const tokenExpiredListeners = new Set<GuestRtcTokenExpiredListener>();
const userStateChangeListeners = new Set<GuestRtcUserStateChangeListener>();
const speakingUids = new Set<string>();
const GUEST_RTC_SESSION_CHANNEL = 'envo_meeting_guest_rtc_session';
const GUEST_RTC_ACTIVE_SESSION_KEY = 'envo_meeting_guest_rtc_active_session';

export function getGuestRtcStorageKey(id: string) {
  return `envo_meeting_guest_rtc:${id}`;
}

export function saveGuestRtcPayload(id: string, payload: any) {
  const rtcPayload: GuestRtcPayload = {
    conferenceId: String(payload?.conferenceId ?? id),
    roomId: String(payload?.roomId ?? ''),
    token: String(payload?.token ?? ''),
    appId: normalizeRtcAppId(payload),
    touristsDuration: String(payload?.touristsDuration ?? ''),
    uid: normalizeRtcUid(payload),
    sessionId: createGuestRtcSessionId(),
  };

  if (!rtcPayload.roomId || !rtcPayload.appId || !rtcPayload.token) {
    return false;
  }

  try {
    window.sessionStorage.setItem(
      getGuestRtcStorageKey(id),
      JSON.stringify(rtcPayload),
    );
  } catch (error) {
    return false;
  }

  return true;
}

function normalizeRtcAppId(payload: any) {
  const appId =
    payload?.rtcAppId ??
    payload?.agoraAppId ??
    payload?.shengwangAppId ??
    payload?.plainAppId ??
    payload?.appID ??
    payload?.app_id ??
    payload?.appId;

  if (typeof appId !== 'string') return '';

  const normalizedAppId = appId.trim();

  if (isValidRtcAppId(normalizedAppId)) {
    return normalizedAppId;
  }

  try {
    const decryptedAppId = rsaPublicDecryptPkcs1(
      normalizedAppId,
      RTC_APP_ID_PUBLIC_KEY,
    ).trim();

    if (isValidRtcAppId(decryptedAppId)) {
      return decryptedAppId;
    }
  } catch (error) {
    console.error('[meeting-rtc] failed to decrypt rtc appId', {
      appIdLength: normalizedAppId.length,
      error,
    });
  }

  return normalizedAppId;
}

function isValidRtcAppId(appId: string) {
  return /^[0-9a-f]{32}$/i.test(appId);
}

function getSafeJoinDiagnostics(payload: GuestRtcPayload) {
  return {
    conferenceId: payload.conferenceId,
    roomId: payload.roomId,
    uid: payload.uid ?? getBrowserFingerprint(),
    appIdLength: payload.appId.length,
    appIdValid: isValidRtcAppId(payload.appId),
    hasToken: Boolean(payload.token),
  };
}

function normalizeRtcUid(payload: any) {
  const uid =
    payload?.uid ??
    payload?.rtcUid ??
    payload?.userId ??
    payload?.touristUid ??
    payload?.clientId;

  if (typeof uid === 'number') return uid;
  if (typeof uid === 'string' && uid.trim()) return uid.trim();

  return getBrowserFingerprint();
}

function createGuestRtcSessionId() {
  try {
    const randomValues = new Uint32Array(2);
    window.crypto?.getRandomValues(randomValues);
    return `guest_${Date.now().toString(36)}_${Array.from(randomValues)
      .map((item) => item.toString(36))
      .join('')}`;
  } catch (error) {
    return `guest_${Date.now().toString(36)}_${Math.random()
      .toString(36)
      .slice(2)}`;
  }
}

export function getGuestRtcPayload(id: string) {
  if (!id) return null;

  try {
    const payload = window.sessionStorage.getItem(getGuestRtcStorageKey(id));

    if (!payload) return null;

    const parsed = JSON.parse(payload) as GuestRtcPayload;

    if (!parsed?.appId || !parsed?.roomId || !parsed?.token) {
      return null;
    }

    return parsed;
  } catch (error) {
    return null;
  }
}

export function removeGuestRtcPayload(id: string) {
  if (!id) return;

  try {
    window.sessionStorage.removeItem(getGuestRtcStorageKey(id));
  } catch (error) {}
}

function getGuestRtcSessionMarker(payload: GuestRtcPayload): GuestRtcSessionMarker {
  return {
    conferenceId: payload.conferenceId,
    roomId: payload.roomId,
    uid: String(payload.uid ?? getBrowserFingerprint()),
    sessionId: payload.sessionId || createGuestRtcSessionId(),
    timestamp: Date.now(),
  };
}

function ensureGuestRtcSessionBroadcastChannel() {
  if (sessionBroadcastChannel || typeof BroadcastChannel === 'undefined') {
    return sessionBroadcastChannel;
  }

  try {
    sessionBroadcastChannel = new BroadcastChannel(GUEST_RTC_SESSION_CHANNEL);
  } catch (error) {
    sessionBroadcastChannel = null;
  }

  return sessionBroadcastChannel;
}

function isSameGuestIdentity(marker: GuestRtcSessionMarker) {
  return (
    currentGuestSession &&
    marker.conferenceId === currentGuestSession.conferenceId &&
    marker.roomId === currentGuestSession.roomId &&
    marker.uid === currentGuestSession.uid &&
    marker.sessionId !== currentGuestSession.sessionId
  );
}

function notifySessionReplaceListeners(marker: GuestRtcSessionMarker) {
  if (!isSameGuestIdentity(marker)) return;

  sessionReplaceListeners.forEach((listener) => listener(marker));
}

function notifyTokenExpiredListeners(reason = 'token-expired') {
  if (!joinedConferenceId) return;

  const state: GuestRtcTokenExpiredState = {
    conferenceId: joinedConferenceId,
    reason,
  };

  tokenExpiredListeners.forEach((listener) => listener(state));
}

function notifyUserStateChangeListeners(
  reason: string,
  uid?: string | number,
) {
  const state: GuestRtcUserStateChange = {
    conferenceId: joinedConferenceId,
    reason,
    uid,
  };

  userStateChangeListeners.forEach((listener) => listener(state));
}

function publishGuestRtcActiveSession(marker: GuestRtcSessionMarker) {
  currentGuestSession = marker;

  try {
    window.localStorage.setItem(
      GUEST_RTC_ACTIVE_SESSION_KEY,
      JSON.stringify(marker),
    );
  } catch (error) {}

  try {
    ensureGuestRtcSessionBroadcastChannel()?.postMessage(marker);
  } catch (error) {}
}

function parseGuestRtcSessionMarker(value: any) {
  try {
    const marker =
      typeof value === 'string' ? JSON.parse(value) : (value as GuestRtcSessionMarker);

    if (
      !marker?.conferenceId ||
      !marker?.roomId ||
      !marker?.uid ||
      !marker?.sessionId
    ) {
      return null;
    }

    return marker;
  } catch (error) {
    return null;
  }
}

export function onGuestRtcSessionReplaced(
  listener: GuestRtcSessionReplaceListener,
) {
  sessionReplaceListeners.add(listener);
  const channel = ensureGuestRtcSessionBroadcastChannel();

  const handleBroadcastMessage = (event: MessageEvent) => {
    const marker = parseGuestRtcSessionMarker(event.data);
    if (marker) notifySessionReplaceListeners(marker);
  };

  const handleStorageMessage = (event: StorageEvent) => {
    if (event.key !== GUEST_RTC_ACTIVE_SESSION_KEY || !event.newValue) return;

    const marker = parseGuestRtcSessionMarker(event.newValue);
    if (marker) notifySessionReplaceListeners(marker);
  };

  channel?.addEventListener('message', handleBroadcastMessage);
  window.addEventListener('storage', handleStorageMessage);

  return () => {
    sessionReplaceListeners.delete(listener);
    channel?.removeEventListener('message', handleBroadcastMessage);
    window.removeEventListener('storage', handleStorageMessage);
  };
}

export function onGuestRtcTokenExpired(listener: GuestRtcTokenExpiredListener) {
  tokenExpiredListeners.add(listener);

  return () => {
    tokenExpiredListeners.delete(listener);
  };
}

export function onGuestRtcUserStateChange(
  listener: GuestRtcUserStateChangeListener,
) {
  userStateChangeListeners.add(listener);

  return () => {
    userStateChangeListeners.delete(listener);
  };
}

async function subscribeRemoteAudio(
  client: IAgoraRTCClient,
  user: IAgoraRTCRemoteUser,
) {
  if (remoteAudioTracks.has(String(user.uid))) return;

  try {
    console.info('[meeting-rtc] subscribing remote audio', {
      uid: user.uid,
      hasAudio: user.hasAudio,
    });
    const audioTrack = await client.subscribe(user, 'audio');
    const track = audioTrack || user.audioTrack;

    if (!track) {
      console.warn('[meeting-rtc] subscribed remote user without audio track', {
        uid: user.uid,
      });
      return;
    }

    remoteAudioTracks.set(String(user.uid), track);
    notifyAudioListeners();
    playRemoteAudio(track, user.uid);
    checkRemoteAudioActive(track, user.uid);
  } catch (error) {
    console.error('[meeting-rtc] failed to subscribe remote audio', {
      uid: user.uid,
      error,
    });
  }
}

async function subscribeRemoteVideo(
  client: IAgoraRTCClient,
  user: IAgoraRTCRemoteUser,
) {
  if (remoteVideoTracks.has(String(user.uid))) {
    notifyVideoListeners();
    return;
  }

  try {
    console.info('[meeting-rtc] subscribing remote video', {
      uid: user.uid,
      hasVideo: user.hasVideo,
    });
    const videoTrack = await client.subscribe(user, 'video');
    const track = videoTrack || user.videoTrack;

    if (!track) {
      console.warn('[meeting-rtc] subscribed remote user without video track', {
        uid: user.uid,
      });
      return;
    }

    remoteVideoTracks.set(String(user.uid), track);
    notifyVideoListeners();
  } catch (error) {
    console.error('[meeting-rtc] failed to subscribe remote video', {
      uid: user.uid,
      error,
    });
  }
}

function bindRtcEvents(client: IAgoraRTCClient) {
  client.on('user-joined', (user) => {
    console.info('[meeting-rtc] remote user joined', getRemoteUserSnapshot(user));
    notifyUserStateChangeListeners('user-joined', user.uid);
  });

  client.on('user-published', (user, mediaType) => {
    console.info('[meeting-rtc] remote user published', {
      ...getRemoteUserSnapshot(user),
      mediaType,
    });

    if (mediaType === 'audio') {
      subscribeRemoteAudio(client, user);
    }

    if (mediaType === 'video') {
      subscribeRemoteVideo(client, user);
    }

    notifyUserStateChangeListeners(`user-published-${mediaType}`, user.uid);
  });

  client.on('published-user-list', (users) => {
    console.info(
      '[meeting-rtc] published user list',
      users.map(getRemoteUserSnapshot),
    );

    users
      .filter((user) => user.hasAudio)
      .forEach((user) => subscribeRemoteAudio(client, user));

    users
      .filter((user) => user.hasVideo)
      .forEach((user) => subscribeRemoteVideo(client, user));

    notifyUserStateChangeListeners('published-user-list');
  });

  client.on('user-unpublished', (user, mediaType) => {
    console.info('[meeting-rtc] remote user unpublished', {
      uid: user.uid,
      hasAudio: user.hasAudio,
      hasVideo: user.hasVideo,
      mediaType,
    });
    if (!mediaType || mediaType === 'audio') {
      remoteAudioTracks.delete(String(user.uid));
      speakingUids.delete(String(user.uid));
      notifyAudioListeners();
    }
    if (!mediaType || mediaType === 'video') {
      remoteVideoTracks.delete(String(user.uid));
      notifyVideoListeners();
    }
    client.unsubscribe(user, mediaType as any).catch(() => {});
    notifyUserStateChangeListeners(
      mediaType ? `user-unpublished-${mediaType}` : 'user-unpublished',
      user.uid,
    );
  });

  client.on('user-left', (user, reason) => {
    console.info('[meeting-rtc] remote user left', {
      uid: user.uid,
      reason,
    });
    remoteAudioTracks.delete(String(user.uid));
    speakingUids.delete(String(user.uid));
    remoteVideoTracks.delete(String(user.uid));
    notifyAudioListeners();
    notifyVideoListeners();
    notifyUserStateChangeListeners(`user-left-${reason || 'unknown'}`, user.uid);
  });

  client.on('user-info-updated', (uid, msg) => {
    console.info('[meeting-rtc] remote user info updated', { uid, msg });
    notifyUserStateChangeListeners(`user-info-updated-${msg}`, uid);
  });

  client.enableAudioVolumeIndicator();
  client.on('volume-indicator', (volumes) => {
    speakingUids.clear();
    volumes.forEach(({ uid, level }) => {
      if (level > 5) {
        speakingUids.add(String(uid));
      }
    });
    notifyAudioListeners();
  });

  client.on('connection-state-change', (curState, prevState, reason) => {
    console.info('[meeting-rtc] connection state changed', {
      prevState,
      curState,
      reason,
    });

    const disconnectReason = String(reason ?? '').toLowerCase();
    if (
      curState === 'DISCONNECTED' &&
      (disconnectReason.includes('token') ||
        disconnectReason.includes('expire'))
    ) {
      notifyTokenExpiredListeners(String(reason ?? 'token-expired'));
    }
  });

  client.on('token-privilege-will-expire', () => {
    console.warn('[meeting-rtc] rtc token privilege will expire');
  });

  client.on('token-privilege-did-expire', () => {
    console.warn('[meeting-rtc] rtc token privilege did expire');
    notifyTokenExpiredListeners('token-privilege-did-expire');
  });
}

function getRemoteUserSnapshot(user: IAgoraRTCRemoteUser) {
  return {
    uid: user.uid,
    hasAudio: user.hasAudio,
    hasVideo: user.hasVideo,
    hasAudioTrack: Boolean(user.audioTrack),
    hasVideoTrack: Boolean(user.videoTrack),
  };
}

function logRemoteUsers(client: IAgoraRTCClient, reason: string) {
  console.info(
    `[meeting-rtc] remote users ${reason}`,
    client.remoteUsers.map(getRemoteUserSnapshot),
  );
}

function subscribeCurrentRemoteAudioUsers(client: IAgoraRTCClient) {
  logRemoteUsers(client, 'snapshot');

  client.remoteUsers
    .filter((user) => user.hasAudio)
    .forEach((user) => subscribeRemoteAudio(client, user));
}

function subscribeCurrentRemoteVideoUsers(client: IAgoraRTCClient) {
  client.remoteUsers
    .filter((user) => user.hasVideo)
    .forEach((user) => subscribeRemoteVideo(client, user));
}

function scheduleRemoteMediaScan(client: IAgoraRTCClient) {
  if (remoteMediaScanTimer) {
    window.clearInterval(remoteMediaScanTimer);
  }

  let count = 0;
  remoteMediaScanTimer = window.setInterval(() => {
    if (!rtcClient || rtcClient !== client) {
      if (remoteMediaScanTimer) window.clearInterval(remoteMediaScanTimer);
      remoteMediaScanTimer = null;
      return;
    }

    count += 1;
    subscribeCurrentRemoteAudioUsers(client);
    subscribeCurrentRemoteVideoUsers(client);

    if (count >= 8 || (remoteAudioTracks.size > 0 && remoteVideoTracks.size > 0)) {
      if (remoteMediaScanTimer) window.clearInterval(remoteMediaScanTimer);
      remoteMediaScanTimer = null;
    }
  }, 1500);
}

function getCurrentVideoEntry() {
  const first = remoteVideoTracks.entries().next();
  if (first.done) return null;
  const [uid, track] = first.value;
  return { uid, track };
}

function notifyVideoListeners() {
  const state = getGuestRtcVideoState();
  videoListeners.forEach((listener) => listener(state));
}

function notifyAudioListeners() {
  const state = getGuestRtcAudioState();
  audioListeners.forEach((listener) => listener(state));
}

export function getGuestRtcVideoState(): GuestRtcVideoState {
  const video = getCurrentVideoEntry();
  return {
    hasVideo: Boolean(video),
    uid: video?.uid,
  };
}

export function onGuestRtcVideoStateChange(listener: GuestRtcVideoListener) {
  videoListeners.add(listener);
  listener(getGuestRtcVideoState());

  return () => {
    videoListeners.delete(listener);
  };
}

export function getGuestRtcAudioState(): GuestRtcAudioState {
  return {
    speakingUids: Array.from(speakingUids),
    unmutedUids: Array.from(remoteAudioTracks.keys()),
  };
}

export function onGuestRtcAudioStateChange(listener: GuestRtcAudioListener) {
  audioListeners.add(listener);
  listener(getGuestRtcAudioState());

  return () => {
    audioListeners.delete(listener);
  };
}

export function playGuestRtcVideo(container: HTMLElement | string) {
  const video = getCurrentVideoEntry();
  if (!video) {
    console.info('[meeting-rtc] remote video play skipped: no video track');
    return false;
  }

  try {
    video.track.play(container, { fit: 'contain' });
    const containerElement =
      typeof container === 'string'
        ? document.getElementById(container)
        : container;

    if (containerElement) {
      normalizeRemoteVideoElement(containerElement);
      window.setTimeout(() => normalizeRemoteVideoElement(containerElement), 100);
      window.setTimeout(() => logRemoteVideoVisibleStatus(video.track, video.uid), 300);
    }

    console.info('[meeting-rtc] remote video play invoked', {
      uid: video.uid,
    });
    return true;
  } catch (error) {
    console.warn('[meeting-rtc] remote video play failed', {
      uid: video.uid,
      error,
    });
    return false;
  }
}

function normalizeRemoteVideoElement(container: HTMLElement) {
  const objectPosition = container.classList.contains('mobile_screen_player')
    ? 'center top'
    : 'center center';

  Array.from(container.children).forEach((child) => {
    const element = child as HTMLElement;
    element.style.maxWidth = '100%';
    element.style.maxHeight = '100%';
    element.style.minWidth = '0';
    element.style.minHeight = '0';
    element.style.flex = '0 1 auto';
    element.style.overflow = 'hidden';
  });

  container.querySelectorAll('video').forEach((videoElement) => {
    normalizeVideoElement(videoElement, objectPosition);
    videoElement.style.margin = '0 auto';
  });
}

function normalizeVideoElement(
  videoElement: HTMLVideoElement,
  objectPosition: string,
) {
  videoElement.setAttribute('playsinline', 'true');
  videoElement.setAttribute('webkit-playsinline', 'true');
  videoElement.setAttribute('autoplay', 'true');
  videoElement.muted = true;
  videoElement.playsInline = true;
  videoElement.style.width = '100%';
  videoElement.style.height = '100%';
  videoElement.style.objectFit = 'contain';
  videoElement.style.objectPosition = objectPosition;
  videoElement.style.display = 'block';
  videoElement.style.backgroundColor = '#0f172a';
}

function logRemoteVideoVisibleStatus(
  track: IRemoteVideoTrack,
  uid: string | number,
) {
  try {
    const status = track.getVideoElementVisibleStatus?.();
    console.info('[meeting-rtc] remote video visible status', {
      uid,
      status,
      statusText: status ? JSON.stringify(status) : '',
    });
  } catch (error) {
    console.warn('[meeting-rtc] remote video visible status failed', {
      uid,
      error,
    });
  }
}

function bindAudioAutoplayHandler() {
  if (audioAutoplayHandlerBound) return;

  audioAutoplayHandlerBound = true;

  AgoraRTC.on('autoplay-failed', () => {
    console.warn('[meeting-rtc] audio autoplay failed, waiting for user gesture');
    bindUserGestureAudioResume();
  });
}

function bindUserGestureAudioResume() {
  const resume = () => {
    resumeGuestRtcAudio();
    window.removeEventListener('click', resume);
    window.removeEventListener('touchstart', resume);
    window.removeEventListener('pointerdown', resume);
  };

  window.addEventListener('click', resume, { once: true });
  window.addEventListener('touchstart', resume, { once: true });
  window.addEventListener('pointerdown', resume, { once: true });
}

function playRemoteAudio(track: IRemoteAudioTrack, uid: string | number) {
  try {
    AgoraRTC.resumeAudioContext?.();
    track.play();
    console.info('[meeting-rtc] remote audio play invoked', { uid });
  } catch (error) {
    console.warn('[meeting-rtc] remote audio play failed', { uid, error });
    bindUserGestureAudioResume();
  }
}

function checkRemoteAudioActive(track: IRemoteAudioTrack, uid: string | number) {
  AgoraRTC.checkAudioTrackIsActive(track, 3000)
    .then((active) => {
      console.info('[meeting-rtc] remote audio active check', { uid, active });
    })
    .catch((error) => {
      console.warn('[meeting-rtc] remote audio active check failed', {
        uid,
        error,
      });
    });
}

export function resumeGuestRtcAudio() {
  try {
    AgoraRTC.resumeAudioContext?.();
  } catch (error) {}

  remoteAudioTracks.forEach((track, uid) => {
    playRemoteAudio(track, uid);
  });
}

export function hasGuestRtcSession(conferenceId: string) {
  return Boolean(rtcClient && joinedConferenceId === conferenceId);
}

export async function joinGuestRtcSession(payload: GuestRtcPayload) {
  if (hasGuestRtcSession(payload.conferenceId)) {
    return;
  }

  await leaveGuestRtcSession();

  if (!isValidRtcAppId(payload.appId)) {
    console.error(
      '[meeting-rtc] invalid appId before joining Shengwang RTC',
      getSafeJoinDiagnostics(payload),
    );
    throw new Error('INVALID_RTC_APP_ID');
  }

  bindAudioAutoplayHandler();

  const client = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });
  rtcClient = client;

  try {
    bindRtcEvents(client);
    await client.join(
      payload.appId,
      payload.roomId,
      payload.token,
      payload.uid ?? getBrowserFingerprint(),
    );
    subscribeCurrentRemoteAudioUsers(client);
    subscribeCurrentRemoteVideoUsers(client);
    scheduleRemoteMediaScan(client);
    joinedConferenceId = payload.conferenceId;
    publishGuestRtcActiveSession(getGuestRtcSessionMarker(payload));
  } catch (error) {
    console.error(
      '[meeting-rtc] failed to join Shengwang RTC',
      getSafeJoinDiagnostics(payload),
      error,
    );
    await leaveGuestRtcSession();
    throw error;
  }
}

export async function leaveGuestRtcSession() {
  if (leavingRtc) return;
  leavingRtc = true;

  try {
    if (remoteAudioScanTimer) {
      window.clearInterval(remoteAudioScanTimer);
      remoteAudioScanTimer = null;
    }

    if (remoteMediaScanTimer) {
      window.clearInterval(remoteMediaScanTimer);
      remoteMediaScanTimer = null;
    }

    if (rtcClient) {
      rtcClient.removeAllListeners();
      await rtcClient.leave();
    }
  } catch (error) {
  } finally {
    rtcClient = null;
    joinedConferenceId = '';
    currentGuestSession = null;
    remoteAudioTracks.clear();
    remoteVideoTracks.clear();
    speakingUids.clear();
    notifyAudioListeners();
    notifyVideoListeners();
    leavingRtc = false;
  }
}
